import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import EmployeeList from './components/EmployeeList';
import AttendanceTracker from './components/AttendanceTracker';
import TaskManager from './components/TaskManager';
import AttendanceCalendar from './components/AttendanceCalendar';
import Reports from './components/Reports';
import { storage, STORAGE_KEYS, getInitialEmployees, getInitialTasks } from './utils/storage';
import './App.css';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load data from localStorage on component mount
  useEffect(() => {
    const loadedEmployees = storage.get(STORAGE_KEYS.EMPLOYEES);
    const loadedAttendance = storage.get(STORAGE_KEYS.ATTENDANCE);
    const loadedTasks = storage.get(STORAGE_KEYS.TASKS);

    // Load employees from localStorage (even if empty array)
    // No default employees - users must add their own
    if (!loadedEmployees) {
      setEmployees([]);
      storage.set(STORAGE_KEYS.EMPLOYEES, []);
    } else {
      setEmployees(loadedEmployees);
    }

    if (!loadedAttendance) {
      setAttendance([]);
      storage.set(STORAGE_KEYS.ATTENDANCE, []);
    } else {
      setAttendance(loadedAttendance);
    }

    // Load tasks from localStorage (even if empty array)
    // No default tasks - users must add their own
    if (!loadedTasks) {
      setTasks([]);
      storage.set(STORAGE_KEYS.TASKS, []);
    } else {
      setTasks(loadedTasks);
    }
    
    // Mark initial load as complete
    setIsInitialLoad(false);
  }, []);

  // Save employees to localStorage whenever employees state changes
  // Always save, even if empty, to persist deletions
  // Skip saving during initial load to avoid overwriting with empty state
  useEffect(() => {
    if (!isInitialLoad) {
      storage.set(STORAGE_KEYS.EMPLOYEES, employees);
    }
  }, [employees, isInitialLoad]);

  // Save attendance to localStorage whenever attendance state changes
  // Always save, even if empty, to persist all changes
  // Skip saving during initial load to avoid overwriting with empty state
  useEffect(() => {
    if (!isInitialLoad) {
      storage.set(STORAGE_KEYS.ATTENDANCE, attendance);
    }
  }, [attendance, isInitialLoad]);

  // Save tasks to localStorage whenever tasks state changes
  // Always save, even if empty, to persist deletions
  // Skip saving during initial load to avoid overwriting with empty state
  useEffect(() => {
    if (!isInitialLoad) {
      storage.set(STORAGE_KEYS.TASKS, tasks);
    }
  }, [tasks, isInitialLoad]);

  // Cascading delete function - deletes employee and all related data
  const handleEmployeeDelete = (employeeId) => {
    // Delete the employee
    setEmployees(employees.filter(emp => emp.id !== employeeId));
    
    // Delete all attendance records for this employee
    setAttendance(attendance.filter(att => att.employeeId !== employeeId));
    
    // Delete all tasks assigned to this employee
    setTasks(tasks.filter(task => task.employeeId !== employeeId));
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard 
            employees={employees}
            attendance={attendance}
            tasks={tasks}
          />
        );
      case 'employees':
        return (
          <EmployeeList 
            employees={employees}
            setEmployees={setEmployees}
            onEmployeeDelete={handleEmployeeDelete}
          />
        );
      case 'attendance':
        return (
          <AttendanceTracker 
            employees={employees}
            attendance={attendance}
            setAttendance={setAttendance}
          />
        );
      case 'tasks':
        return (
          <TaskManager 
            employees={employees}
            tasks={tasks}
            setTasks={setTasks}
          />
        );
      case 'calendar':
        return (
          <AttendanceCalendar 
            employees={employees}
            attendance={attendance}
          />
        );
      case 'reports':
        return (
          <Reports 
            employees={employees}
            attendance={attendance}
            tasks={tasks}
          />
        );
      default:
        return (
          <Dashboard 
            employees={employees}
            attendance={attendance}
            tasks={tasks}
          />
        );
    }
  };

  return (
    <ThemeProvider>
      <div className="App">
        <Layout activeTab={activeTab} setActiveTab={setActiveTab}>
          {renderContent()}
        </Layout>
      </div>
    </ThemeProvider>
  );
}

export default App;